import { useEffect, useRef } from 'react';
import { useChartContextStore } from 'src/zustandStores/useChartContextStore';
import { shouldShowAtlasChartIntegration, shouldShowBeaconAdvisor, getAppName } from 'src/utils/app';
import { useLocation } from 'src/utils/Hooks/typedHooks';
import { buildUrlSuffix } from './useBuildChartContextId';
import { useChartContextGroup, useChartContextName } from './ChartContextGroupContext';
import type { ChartLegendMetadata } from './types';

/**
 * Build the full chart context ID in a single step.
 *
 * Uses `#` as the delimiter between top-level parts so that the ID is
 * unambiguously parseable via `split('#')`. Hyphens within individual
 * part names (e.g. `traffic-donut`) are preserved.
 *
 * Format (with contextGroup): {app}#{contextGroup}#{chartName}#{urlSuffix}
 * Format (no contextGroup):   {app}#{chartName}#{urlSuffix}
 *
 * Examples:
 * - atlas#traffic#traffic-donut#category/1280?rid=1&tab=trafficV2&subtab=totalClicks
 * - atlas#traffic#traffic-bar-current#category/1280?rid=1&tab=trafficV2&subtab=totalClicks
 */
const buildFullChartId = (
  chartName: string,
  pathname: string,
  search: string,
  contextGroup?: string
): string => {
  const app = getAppName() || 'unknown';
  const parts: string[] = [app];
  if (contextGroup) parts.push(contextGroup);
  parts.push(chartName);
  const prefix = parts.join('#');
  const urlSuffix = buildUrlSuffix(pathname, search);
  return `${prefix}#${urlSuffix}`;
};

/**
 * Hook to register chart options with the chart context store.
 *
 * This is the single entry point for chart registration. It builds the full
 * chart ID internally from the raw chart name, current URL, and context group
 * (from React Context). Callers only need to pass a chart name constant from
 * the registry -- no need to call useChartContextId separately.
 *
 * Behavior:
 * - Only registers when Atlas chart integration feature is enabled
 * - Automatically unregisters on unmount
 * - Updates registration when options or URL change
 * - Reads contextGroup from React Context (ChartContextGroupProvider at layout level)
 * - Chart ID format: {app}#{contextGroup}#{chartName}#{urlSuffix}
 *
 * @param chartName - Raw chart name from the registry (e.g., 'traffic-donut').
 *   Use constants from chartContextRegistry.ts (CHART_NAMES.TRAFFIC_DONUT).
 *   If undefined, falls back to the chartName from ChartContextProvider.
 * @param options - Highcharts options object to register
 * @param enabled - Whether registration is enabled (default: true)
 * @param legendMetadata - Optional pre-computed legend summary values (main period value,
 *   comparison value, percent change, direction). Provided by AtlasV2DorisSplineChart
 *   for spline charts so the agent can read the displayed badge values directly.
 *
 * @example
 * ```tsx
 * import { CHART_NAMES } from 'src/utils/chartContext/chartContextRegistry';
 *
 * // Option A: chartName provided via prop (multi-chart groups)
 * useRegisterChartContext(CHART_NAMES.TRAFFIC_DONUT, highchartsOptions);
 *
 * // Option B: chartName from ChartContextProvider (1:1 sections, no prop needed)
 * useRegisterChartContext(undefined, highchartsOptions);
 *
 * // Option C: with legend metadata (spline charts via AtlasV2DorisSplineChart)
 * useRegisterChartContext(undefined, highchartsOptions, true, legendMetadata);
 * ```
 */
export const useRegisterChartContext = (
  chartName: string | undefined,
  options: Record<string, unknown> | null | undefined,
  enabled: boolean = true,
  legendMetadata?: ChartLegendMetadata
): void => {
  const registerChart = useChartContextStore((state) => state.registerChart);
  const unregisterChart = useChartContextStore((state) => state.unregisterChart);

  // Read context group from React Context (ChartContextGroupProvider at layout level)
  const contextGroup = useChartContextGroup();

  // Read chart name from React Context as fallback when prop is undefined
  const chartNameFromCtx = useChartContextName();
  const resolvedChartName = chartName ?? chartNameFromCtx;

  // React Router location for building URL suffix
  const location = useLocation();

  // Keep track of the registered chart ID for cleanup
  const registeredChartIdRef = useRef<string | null>(null);

  useEffect(() => {
    // Skip if feature is disabled, no chartName resolved, no options, or explicitly disabled
    if ((!shouldShowAtlasChartIntegration() && !shouldShowBeaconAdvisor()) || !resolvedChartName || !options || !enabled) {
      // Clean up if previously registered
      if (registeredChartIdRef.current) {
        unregisterChart(registeredChartIdRef.current);
        registeredChartIdRef.current = null;
      }
      return;
    }

    // Build the full chart ID in one step:
    // {app}#{contextGroup}#{chartName}#{pathname}?{queryParams}
    const chartId = buildFullChartId(resolvedChartName, location.pathname, location.search, contextGroup);

    // Register the chart with its context group and optional legend metadata
    registerChart(chartId, options, contextGroup, legendMetadata);
    registeredChartIdRef.current = chartId;

    // Cleanup on unmount or when dependencies change
    return () => {
      if (registeredChartIdRef.current) {
        unregisterChart(registeredChartIdRef.current);
        registeredChartIdRef.current = null;
      }
    };
  }, [resolvedChartName, options, enabled, legendMetadata, registerChart, unregisterChart, contextGroup, location.pathname, location.search]);
};
